import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PulseemReactInstance } from '../../helpers/Api/PulseemReactAPI';

// --- Interfaces --- //
export interface PopupStep {
  StepNumber: number;
  HtmlContent?: string;
  JsonData?: string;
}

interface TopPerformer {
  Id: number;
  Name: string;
  ConversionRate: number;
}

interface PerformanceStats {
  TotalPopups: number;
  ActiveCount: number;
  InactiveCount: number;
  DraftCount: number;
  MonthlyViews: number;
  MonthlyViewsChange: number;
  AverageConversionRate: number;
  AverageConversionChange: number;
  TopPerformer: TopPerformer;
}

export interface Page {
  ID: number;
  Name: string;
  Status: number;
  StatusName: string;
  AllViews: number;
  DesktopViewsPercent: number;
  MobileViewsPercent: number;
  IdentifiedViewers: number;
  IdentifiedViewersPercent: number;
  Conversions: number;
  IdentifiedConversions: number;
  ConversionRate: number;
  ConversionRateChange: number;
  ConversionType: number;
  CreatedDate: string;
  LastModifiedDate: string;
  Domains: string[];
  PopupGuid: string;
  IsSurvey?: boolean;
  SurveyCount?: number;
  IsNewEditor?: boolean;
  Submits?: number;
}

interface PopUpManagementState {
  stats: PerformanceStats | null;
  statsLoading: boolean;
  statsError: string | null;
  pages: Page[];
  totalPages: number;
  currentPage: number;
  pagesLoading: boolean;
  pagesError: string | null;
  deletedPopups: Page[];
  steps: PopupStep[];
  stepsLoading: boolean;
  stepsError: string | null;
}

// --- Initial State --- //
const initialState: PopUpManagementState = {
  stats: null,
  statsLoading: false,
  statsError: null,
  pages: [],
  totalPages: 1,
  currentPage: 1,
  pagesLoading: false,
  pagesError: null,
  deletedPopups: [],
  steps: [],
  stepsLoading: false,
  stepsError: null,
};

// --- Async Thunks --- //
export const getPerformanceStats = createAsyncThunk(
  'popUpManagement/getPerformanceStats',
  async (_, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.get('popup/GetPerformanceStats');
      return response.data.Data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

interface PopupPagesResponse {
  Pages: Page[];
  TotalPages: number;
  CurrentPage: number;
}

interface Filters {
  SearchTerm: string;
  FilterStatus: string;
  SortBy: string;
  SortDirection: string;
  PageNumber: number;
  PageSize: number;
}

export const getPopupPages = createAsyncThunk<PopupPagesResponse, Filters>(
  'popUpManagement/getPopupPages',
  async (filters, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.post('popup/GetPopupPages', filters);
      return response.data.Data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const togglePopupStatus = createAsyncThunk(
  'popUpManagement/togglePopupStatus',
  async ({ ID, Status }: { ID: number; Status: number }, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.post('popup/ToggleStatus', { ID, Status });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const deletePopup = createAsyncThunk<number, number>(
  'popUpManagement/deletePopup',
  async (id: number, thunkAPI) => {
    try {
      await PulseemReactInstance.delete(`landingpages/deleteLandingPage/${id}`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const getDeletedPopups = createAsyncThunk(
  'popUpManagement/getDeletedPopups',
  async (_, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.get('popup/GetDeletedPopups');
      return response.data.Data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const getPopupSteps = createAsyncThunk(
  'popUpManagement/getPopupSteps',
  async (webFormId: number, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.get(`popup/GetPopupSteps`, { params: { webFormId } });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const addPopupStep = createAsyncThunk(
  'popUpManagement/addPopupStep',
  async (webFormId: number, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.post('popup/AddPopupStep', { WebFormId: webFormId });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const deletePopupStep = createAsyncThunk(
  'popUpManagement/deletePopupStep',
  async ({ webFormId, stepNumber }: { webFormId: number; stepNumber: number }, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.post('popup/DeletePopupStep', { WebFormId: webFormId, StepNumber: stepNumber });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

export const savePopupStepContent = createAsyncThunk(
  'popUpManagement/savePopupStepContent',
  async ({ webFormId, stepNumber, htmlContent, jsonData }: { webFormId: number; stepNumber: number; htmlContent: string; jsonData: string }, thunkAPI) => {
    try {
      const response = await PulseemReactInstance.post('popup/SavePopupStepContent', { WebFormId: webFormId, StepNumber: stepNumber, HtmlContent: htmlContent, JsonData: jsonData });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: (error as Error).message });
    }
  }
);

// --- Slice --- //
const popUpManagementSlice = createSlice({
  name: 'popUpManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Stats Reducers
      .addCase(getPerformanceStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(getPerformanceStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(getPerformanceStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = (action.payload as { error: string }).error;
      })
      // Pages Reducers
      .addCase(getPopupPages.pending, (state) => {
        state.pagesLoading = true;
        state.pagesError = null;
      })
      .addCase(getPopupPages.fulfilled, (state, action) => {
        state.pagesLoading = false;
        state.pages = action.payload.Pages;
        state.totalPages = action.payload.TotalPages;
        state.currentPage = action.payload.CurrentPage;
      })
      .addCase(getPopupPages.rejected, (state, action) => {
        state.pagesLoading = false;
        state.pagesError = (action.payload as { error: string }).error;
      })
      // Toggle Status Reducers
      .addCase(togglePopupStatus.pending, (state) => {
        state.pagesLoading = true;
      })
      .addCase(togglePopupStatus.fulfilled, (state, action) => {
        state.pagesLoading = false;
        if (action.payload.StatusCode !== 927) {
          const { ID, Status } = action.meta.arg;
          const index = state.pages.findIndex(p => p.ID === ID);
          if (index !== -1) {
            if (Status === 2) {
              state.pages[index].StatusName = 'Active';
              state.pages[index].Status = 1;
            } else {
              state.pages[index].StatusName = 'Inactive';
              state.pages[index].Status = 0;
            }
          }
        }
      })
      .addCase(togglePopupStatus.rejected, (state, action) => {
        state.pagesLoading = false;
        state.pagesError = (action.payload as { error: string }).error;
      })
      // Delete Popup Reducers
      .addCase(deletePopup.pending, (state) => {
        state.pagesLoading = true;
      })
      .addCase(deletePopup.fulfilled, (state, action) => {
        state.pagesLoading = false;
        state.pages = state.pages.filter(p => p.ID !== action.payload);
      })
      .addCase(deletePopup.rejected, (state, action) => {
        state.pagesLoading = false;
        state.pagesError = (action.payload as { error: string }).error;
      })
      // Deleted Popups Reducers
      .addCase(getDeletedPopups.pending, (state) => {
        state.pagesLoading = true;
      })
      .addCase(getDeletedPopups.fulfilled, (state, action) => {
        state.pagesLoading = false;
        state.deletedPopups = action.payload;
      })
      .addCase(getDeletedPopups.rejected, (state, action) => {
        state.pagesLoading = false;
        state.pagesError = (action.payload as { error: string }).error;
      })
      // Step Reducers
      .addCase(getPopupSteps.pending, (state) => {
        state.stepsLoading = true;
        state.stepsError = null;
      })
      .addCase(getPopupSteps.fulfilled, (state, action) => {
        state.stepsLoading = false;
        state.steps = action.payload?.Data || [];
      })
      .addCase(getPopupSteps.rejected, (state, action) => {
        state.stepsLoading = false;
        state.stepsError = (action.payload as { error: string })?.error || null;
      })
      .addCase(addPopupStep.pending, (state) => {
        state.stepsLoading = true;
        state.stepsError = null;
      })
      .addCase(addPopupStep.fulfilled, (state) => {
        state.stepsLoading = false;
      })
      .addCase(addPopupStep.rejected, (state, action) => {
        state.stepsLoading = false;
        state.stepsError = (action.payload as { error: string })?.error || null;
      })
      .addCase(deletePopupStep.pending, (state) => {
        state.stepsLoading = true;
        state.stepsError = null;
      })
      .addCase(deletePopupStep.fulfilled, (state) => {
        state.stepsLoading = false;
      })
      .addCase(deletePopupStep.rejected, (state, action) => {
        state.stepsLoading = false;
        state.stepsError = (action.payload as { error: string })?.error || null;
      })
      .addCase(savePopupStepContent.pending, (state) => {
        state.stepsLoading = true;
        state.stepsError = null;
      })
      .addCase(savePopupStepContent.fulfilled, (state) => {
        state.stepsLoading = false;
      })
      .addCase(savePopupStepContent.rejected, (state, action) => {
        state.stepsLoading = false;
        state.stepsError = (action.payload as { error: string })?.error || null;
      });
  },
});

export default popUpManagementSlice.reducer;